/** @jsx jsx */
import { type ComponentType, type CSSProperties, forwardRef, useCallback, Fragment } from 'react';
import { cssMap, jsx } from '@compiled/react';
import LeftArrow from '@atlaskit/icon/core/arrow-left';
import {
	type CustomItemComponentProps,
	type CustomItemProps,
	NestingItem,
} from '@atlaskit/side-navigation';
import { SidebarErrorBoundary } from '../../common/ui/error-boundary/index.tsx';
import { MenuItem } from '../../common/ui/menu-item/main.tsx';
import { useSidebarTheme } from '../../controllers/sidebar-theme/index.tsx';
import { isNormalClick } from '../../utils/is-normal-click.tsx';
import type { NestingMenuItemProps } from './types.tsx';

const iconSecondaryColorCssVar = '--nesting-menu-item-icon-secondary-color';

const styles = cssMap({
	backButtonThemeOverrides: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'[data-item-elem-before], &:active span[data-item-elem-before]': {
			color: `var(${iconSecondaryColorCssVar})`,
		},
	},
	customItem: {
		height: '44px',
		/* fixes CSS leaking from the monolith */
		'&:visited, &:focus, &:active': {
			textDecoration: 'inherit',
			outline: 0,
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'[data-item-elem-after] [data-right-arrow]': {
			display: 'none',
		},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'&:hover [data-item-elem-after]': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors
			'> :not([data-right-arrow])': {
				display: 'none',
			},
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
			'[data-right-arrow]': {
				display: 'flex',
			},
		},
	},
});

const WrappingLinkComponent: ComponentType<CustomItemComponentProps> = (
	props: CustomItemComponentProps, // eslint-disable-next-line @atlaskit/design-system/no-html-anchor
) => <a {...props}>{props.children}</a>;

export const NestingMenuItem = forwardRef<HTMLElement, NestingMenuItemProps>(
	({ children, id, reloadOnNavigationToNesting, overrides = {}, title, ...rest }, ref) => {
		const theme = useSidebarTheme();
		const iconSecondaryColor = theme?.navigation?.icon?.secondary?.color?.default;
		const { BackMenuItem = {} } = overrides;
		const {
			title: backTitle = 'Back',
			wrapper: WrapperComponent = Fragment,
			...backItemProps
		} = BackMenuItem as NonNullable<NestingMenuItemProps['overrides']['BackMenuItem']>; // eslint-disable-line @typescript-eslint/consistent-type-assertions

		const renderBackItem = useCallback(
			({ onClick }: { onClick: () => void }) => (
				<WrapperComponent>
					<MenuItem
						{...backItemProps}
						analytics={{ itemId: 'back' }}
						iconBefore={<LeftArrow spacing="spacious" label="" />}
						onClick={(ev) => {
							if (isNormalClick(ev)) {
								onClick();
								backItemProps.onClick?.(ev);
							}
						}}
						css={iconSecondaryColor && styles.backButtonThemeOverrides}
						// eslint-disable-next-line jira/react/no-style-attribute, @typescript-eslint/consistent-type-assertions
						style={{ [iconSecondaryColorCssVar]: iconSecondaryColor ?? undefined } as CSSProperties}
					>
						{backTitle}
					</MenuItem>
				</WrapperComponent>
			),
			[WrapperComponent, backItemProps, backTitle, iconSecondaryColor],
		);

		const renderCustomItem = useCallback(
			({ children: itemTitle, onClick, className, ...props }: CustomItemProps) => (
				<NestingItem
					{...props}
					id={id}
					component={reloadOnNavigationToNesting ? WrappingLinkComponent : props.component}
					onClick={(ev) => {
						// if nesting item already selected do not trigger route change
						if (props.isSelected === true) ev.preventDefault();
						onClick && onClick(ev);
					}}
					// eslint-disable-next-line @atlaskit/design-system/no-deprecated-apis
					overrides={{ GoBackItem: { render: renderBackItem } }}
					title={itemTitle}
					// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
					css={styles.customItem}
					// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop, @atlaskit/design-system/no-unsafe-style-overrides
					className={className}
				>
					<SidebarErrorBoundary id={`${id}Nesting`} packageName="jiraSpaAppsSidebarCommon">
						{children}
					</SidebarErrorBoundary>
				</NestingItem>
			),
			[children, id, reloadOnNavigationToNesting, renderBackItem],
		);

		return (
			<MenuItem {...rest} ref={ref} overrides={{ CustomItem: { render: renderCustomItem } }}>
				{title}
			</MenuItem>
		);
	},
);
